import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Observable } from 'rxjs';
import { combineLatestWith, map, take } from 'rxjs/operators';
import { ProductAdditions, ProductDetails, ProductLine } from 'src/app/models';
import { ProductAdditionsComponent } from 'src/app/pages/products';
import { TextButtonComponent, ToggleComponent } from 'src/app/shared/components';
import { DisplayCurrencyPipe } from '../../../../shared/pipes/display-currency.pipe';
import { ConfirmationDialogData } from '../../checkout/complete-step/confirmation-dialog/confirmation-dialog.compontent';
import { BasketOverviewUtility } from '../basket-overview.utility';
import { MenyBasketItemComponent } from './meny-basket-item/meny-basket-item.component';

@Component({
  selector: 'app-meny-basket-overview',
  templateUrl: './meny-basket-overview.component.html',
  styleUrls: ['./meny-basket-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TextButtonComponent,
    MatIconModule,
    AsyncPipe,
    MenyBasketItemComponent,
    DecimalPipe,
    DisplayCurrencyPipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    DatePipe,
    ToggleComponent,
    ProductAdditionsComponent,
  ],
})
export class MenyBasketOverviewComponent extends BasketOverviewUtility implements OnInit {
  confirmationData: ConfirmationDialogData;
  productLines$: Observable<ProductLine[]>;

  shippingName: string;

  constructor() {
    super();

    this.confirmationData = {
      title: 'Er du sikker på at du vil tømme din kurv?',
      description: '',
      acceptButton: 'Tøm kurv',
      rejectButton: 'Tilbage',
    };
  }

  override ngOnInit() {
    super.ngOnInit();

    this.productLines$ = this.basketQuery.selectProductLines$.pipe(
      combineLatestWith(this.basketQuery.selectErrorProductIds$),
      map(([lines, invalidProductIds]) => {
        const productLines: ProductLine[] = [];

        for (const line of lines) {
          const product = this.basketService.getProduct(line);
          if (!product) {
            continue;
          }
          const error = invalidProductIds.includes(line.productId);

          const entry = { ...line, product, error };
          productLines.push(entry);
        }

        return productLines;
      }),
    );

    this.subscription.add(
      this.shipping$.pipe().subscribe((s) => {
        const options = this.shippingService.getShippingOptions();
        this.shippingName = options.find((o) => o.id === s.shippingType)?.name;
      }),
    );

    this.cdr.detectChanges();
  }

  calculateTax(...prices: number[]): number {
    let tax = 0;

    for (const price of prices) {
      if (price) {
        tax += price * 0.2;
      }
    }

    return tax;
  }

  removeInvalidBasket() {
    const ids = this.basketQuery.getErrorProductIds();
    this.productLines$.pipe(take(1)).subscribe((productLines) => {
      for (const line of productLines) {
        if (ids.includes(line.productId)) {
          this.basketService.deleteProduct(line.product);
        }
      }
    });
  }

  resetBasket() {
    this.checkoutService
      .openConfirmationDialog(this.confirmationData)
      .afterClosed()
      .pipe(take(1))
      .subscribe((accepted) => {
        if (accepted) {
          this.basketService.resetBasket();
        }
      });

    return;
  }

  getProductAdditions(productLines: ProductLine[]): ProductAdditions {
    const additions: ProductAdditions = {
      id: 0,
      name: 'EKSTRA TILVALG',
      display: false,
      carousel: false,
      products: [],
    };

    productLines?.forEach((line) => {
      const product = line.product as ProductDetails;
      product.productAdditions?.forEach((productAddition) => {
        if (productAddition.display) {
          additions.products.push(...productAddition.products);
        }
      });
    });

    additions.display = additions.products.length > 0;
    // Remove duplicates
    additions.products = additions.products.filter((value, index, self) => index === self.findIndex((t) => t.id === value.id));

    return additions;
  }
}
